import API from "@aws-amplify/api";
import { createChat, deleteChat, updateChat } from "../../graphql/mutations";
import { chatsByWorkspace, getChat } from "../../graphql/queries";
import { makeGraphQLRequest } from "..";
//
const createChat_ = async (object) => {
  return makeGraphQLRequest({
    query: createChat,
    variables: { input: object },
  });
};
const getChat_ = async (id) => {
  return makeGraphQLRequest({
    query: getChat,
    variables: { id },
  });
};
const updateChat_ = async (id, args) => {
  const original = await getChat_(id);
  let _properties = {};
  args.forEach((item) => {
    _properties[item.property] = item.value;
  });
  return makeGraphQLRequest({
    query: updateChat,
    variables: {
      input: {
        id,
        _version: original?.data?.getChat?._version,
        ..._properties,
      },
    },
  });
};

const deleteChat_ = async (id) => {
  const original = await getChat_(id);
  return makeGraphQLRequest({
    query: deleteChat,
    variables: {
      input: {
        id,
        _version: original?.data?.getChat?._version,
      },
    },
  });
};

const getChats = async (workspaceID, nextToken, isWidget) => {
  return makeGraphQLRequest({
    query: chatsByWorkspace,
    variables: {
      workspaceID,
      nextToken,
      sortDirection: "DESC",
      limit: 1500,
      filter: {
        or: [
          {
            handedOffToHuman: {
              eq: true,
            },
          },
          {
            name: {
              ne: null,
            },
          },
        ],
      },
    },
  });
};

const getChatAnalytics = async (workspaceID, limit = 5000, filters = {}) => {
  const { startDate, endDate, agentId } = filters;
  let allChats = [];
  let nextToken = null;
  let pageCount = 0;

  do {
    pageCount++;
    const result = await makeGraphQLRequest({
      query: chatsByWorkspace,
      variables: {
        workspaceID,
        nextToken,
        sortDirection: "DESC",
        limit: Math.min(limit - allChats.length, 500),
      },
    });

    const items = result?.data?.chatsByWorkspace?.items || [];

    allChats = [...allChats, ...items];
    nextToken = result?.data?.chatsByWorkspace?.nextToken;
  } while (nextToken && allChats.length < limit);

  // Apply filters
  let filteredChats = allChats;

  // Filter by date range using creationTime (AWSTimestamp in seconds)
  if (startDate || endDate) {
    const startTimestamp = startDate
      ? Math.floor(new Date(startDate).getTime() / 1000)
      : null;
    const endTimestamp = endDate
      ? Math.floor(new Date(endDate).setHours(23, 59, 59, 999) / 1000)
      : null;

    filteredChats = filteredChats.filter((chat) => {
      const chatTimestamp = chat.creationTime;
      if (!chatTimestamp) return true; // Include chats without creationTime
      if (startTimestamp && chatTimestamp < startTimestamp) return false;
      if (endTimestamp && chatTimestamp > endTimestamp) return false;
      return true;
    });
  }

  // Filter by agent
  if (agentId && agentId !== "all") {
    filteredChats = filteredChats.filter(
      (chat) => chat.primaryRole === agentId
    );
  }

  const result = processChatsForAnalytics(filteredChats);

  return { data: result, rawChats: filteredChats };
};

const processChatsForAnalytics = (chats) => {
  const validChats = chats.filter((chat) => !chat._deleted);

  // PII Statistics
  const piiStats = {
    detected: validChats.filter((c) => c.piiDetected).length,
    totalOccurrences: validChats.reduce(
      (sum, c) => sum + (c.piiOccurrences || 0),
      0
    ),
  };

  // PII breakdown by type
  const piiBreakdown = {};
  validChats.forEach((chat) => {
    if (chat.piiTypes && Array.isArray(chat.piiTypes)) {
      chat.piiTypes.forEach((type) => {
        piiBreakdown[type] = (piiBreakdown[type] || 0) + 1;
      });
    }
  });

  // Toxicity Statistics
  const toxicChats = validChats.filter((c) => c.toxicityDetected);
  const toxicityScores = validChats
    .filter((c) => c.toxicityScore !== null && c.toxicityScore !== undefined)
    .map((c) => c.toxicityScore);

  const toxicityStats = {
    detected: toxicChats.length,
    averageScore:
      toxicityScores.length > 0
        ? toxicityScores.reduce((a, b) => a + b, 0) / toxicityScores.length
        : 0,
  };

  // Toxicity breakdown by category
  const toxicityBreakdown = {};
  validChats.forEach((chat) => {
    if (chat.toxicityCategories && Array.isArray(chat.toxicityCategories)) {
      chat.toxicityCategories.forEach((category) => {
        toxicityBreakdown[category] = (toxicityBreakdown[category] || 0) + 1;
      });
    }
  });

  // Customer Satisfaction Statistics
  const satisfactionScores = validChats
    .filter(
      (c) =>
        c.customerSatisfactionScore !== null &&
        c.customerSatisfactionScore !== undefined
    )
    .map((c) => c.customerSatisfactionScore);

  // Normalize scores - they can be 0-1 or 0-10, normalize to 0-10
  const normalizedScores = satisfactionScores.map((s) => (s <= 1 ? s * 10 : s));

  const averageSatisfaction =
    normalizedScores.length > 0
      ? normalizedScores.reduce((a, b) => a + b, 0) / normalizedScores.length
      : null;
  const satisfactionDistribution = {
    veryLow: normalizedScores.filter((s) => s >= 0 && s < 2).length,
    low: normalizedScores.filter((s) => s >= 2 && s < 4).length,
    medium: normalizedScores.filter((s) => s >= 4 && s < 6).length,
    high: normalizedScores.filter((s) => s >= 6 && s < 8).length,
    veryHigh: normalizedScores.filter((s) => s >= 8 && s <= 10).length,
  };

  // Request Completion Statistics
  const completionStats = {
    completed: validChats.filter((c) => c.requestCompleted === true).length,
    incomplete: validChats.filter((c) => c.requestCompleted === false).length,
    unknown: validChats.filter(
      (c) => c.requestCompleted === null || c.requestCompleted === undefined
    ).length,
  };

  // Completion scores
  const completionScores = validChats
    .filter(
      (c) =>
        c.requestCompletionScore !== null &&
        c.requestCompletionScore !== undefined
    )
    .map((c) => c.requestCompletionScore);

  const averageCompletionScore =
    completionScores.length > 0
      ? completionScores.reduce((a, b) => a + b, 0) / completionScores.length
      : null;

  // Escalation Statistics
  const escalatedChats = validChats.filter((c) => c.escalationRequired);
  const escalationStats = {
    total: escalatedChats.length,
    percentage:
      validChats.length > 0
        ? (escalatedChats.length / validChats.length) * 100
        : 0,
  };

  // Escalation reasons breakdown
  const escalationReasonsMap = {};
  escalatedChats.forEach((chat) => {
    const reason = chat.escalationReason || "Unknown";
    escalationReasonsMap[reason] = (escalationReasonsMap[reason] || 0) + 1;
  });
  const escalationReasons = Object.entries(escalationReasonsMap)
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);

  // Quality Score Distribution - handle various score labels
  const qualityDistribution = {
    excellent: 0,
    good: 0,
    fair: 0,
    poor: 0,
    unrated: 0,
  };

  validChats.forEach((chat) => {
    const score = chat.qualityScore?.toLowerCase();
    if (score === "excellent" || score === "very high") {
      qualityDistribution.excellent++;
    } else if (score === "good" || score === "high") {
      qualityDistribution.good++;
    } else if (score === "fair" || score === "medium" || score === "average") {
      qualityDistribution.fair++;
    } else if (score === "poor" || score === "low" || score === "very low") {
      qualityDistribution.poor++;
    } else {
      qualityDistribution.unrated++;
    }
  });

  return {
    totalChatsAnalyzed: validChats.length,
    piiStats,
    piiBreakdown,
    toxicityStats,
    toxicityBreakdown,
    averageSatisfaction,
    satisfactionDistribution,
    completionStats,
    averageCompletionScore,
    escalationStats,
    escalationReasons,
    qualityDistribution,
  };
};

export default {
  createChat: createChat_,
  updateChat: updateChat_,
  deleteChat: deleteChat_,
  getChats,
  getChat: getChat_,
  getChatAnalytics,
};
